import React, { useRef, useState, useEffect, useCallback } from 'react';
import { X, ArrowsOutCardinal } from '@phosphor-icons/react';

// ─────────────────────────────────────────────────────────────────────────────
// CoverCropper — a dependency-free image cropper for the vendor cover photo.
// The vendor drags to reposition and uses the zoom slider to frame the exact
// area; on "Use this", we render the visible crop to a canvas at the cover
// aspect ratio and hand back a JPEG Blob to upload. No external crop library
// (keeps the bundle lean and avoids peer-dep conflicts).
//
// Aspect ratio matches how the cover is displayed on Browse + the profile
// header (a wide 16:9 banner). Output width is capped so uploads stay small.
// ─────────────────────────────────────────────────────────────────────────────

// Match the shape customers ACTUALLY see on the Browse card (image container is
// h-44 ≈ 176px tall in a ~370px-wide card ≈ 2.1:1) — NOT a generic 16:9, which
// is why covers were getting cropped top/bottom. The vendor frames exactly what
// gets shown.
const ASPECT = 2.1;             // Browse-card cover shape (width : height)
const OUTPUT_W = 1280;          // exported width (height derived from ASPECT)
const FRAME_W = 480;            // on-screen crop frame width (px); height = /ASPECT

export default function CoverCropper({ file, onCancel, onCropped }) {
  const [imgUrl, setImgUrl] = useState(null);
  const [img, setImg] = useState(null);        // loaded HTMLImageElement
  const [zoom, setZoom] = useState(1);
  const [minZoom, setMinZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });   // top-left of image within frame, px
  const [busy, setBusy] = useState(false);
  const drag = useRef(null);
  const frameH = FRAME_W / ASPECT;

  // Load the picked file into an Image element.
  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setImgUrl(url);
    const im = new Image();
    im.onload = () => setImg(im);
    im.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Once loaded, compute the minimum zoom so the image always fully covers the
  // frame (no empty gaps), and center it.
  useEffect(() => {
    if (!img) return;
    const scaleToCover = Math.max(FRAME_W / img.width, frameH / img.height);
    setMinZoom(scaleToCover);
    setZoom(scaleToCover);
    const w = img.width * scaleToCover;
    const h = img.height * scaleToCover;
    setOffset({ x: (FRAME_W - w) / 2, y: (frameH - h) / 2 });
  }, [img, frameH]);

  // Keep the image covering the frame after any zoom/drag (clamp offsets).
  const clamp = useCallback((off, z) => {
    if (!img) return off;
    const w = img.width * z;
    const h = img.height * z;
    const minX = FRAME_W - w, minY = frameH - h;
    return {
      x: Math.min(0, Math.max(minX, off.x)),
      y: Math.min(0, Math.max(minY, off.y)),
    };
  }, [img, frameH]);

  const onPointerDown = (e) => {
    drag.current = { sx: e.clientX, sy: e.clientY, ox: offset.x, oy: offset.y };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    const next = { x: drag.current.ox + (e.clientX - drag.current.sx), y: drag.current.oy + (e.clientY - drag.current.sy) };
    setOffset(clamp(next, zoom));
  };
  const onPointerUp = () => { drag.current = null; };

  const onZoom = (e) => {
    const z = Math.max(minZoom, Number(e.target.value));
    // Zoom around the frame center so it feels natural.
    const cx = FRAME_W / 2, cy = frameH / 2;
    const ratio = z / zoom;
    const next = { x: cx - (cx - offset.x) * ratio, y: cy - (cy - offset.y) * ratio };
    setZoom(z);
    setOffset(clamp(next, z));
  };

  // Render the framed area to a canvas → JPEG Blob.
  const apply = async () => {
    if (!img) return;
    setBusy(true);
    try {
      const outH = Math.round(OUTPUT_W / ASPECT);
      const canvas = document.createElement('canvas');
      canvas.width = OUTPUT_W; canvas.height = outH;
      const ctx = canvas.getContext('2d');
      // Map frame-space to output-space.
      const scale = OUTPUT_W / FRAME_W;
      // Source rect in original-image pixels that currently fills the frame:
      const sx = -offset.x / zoom;
      const sy = -offset.y / zoom;
      const sw = FRAME_W / zoom;
      const sh = frameH / zoom;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, OUTPUT_W, outH);
      const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.9));
      const cropped = new File([blob], 'cover.jpg', { type: 'image/jpeg' });
      onCropped(cropped);
    } catch (err) {
      console.error('Crop failed:', err);
      onCancel();
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-foreground/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="cover-cropper">
      <div className="bg-card rounded-2xl shadow-lift border border-border w-full max-w-lg p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-foreground">Frame your cover photo</h3>
          <button onClick={onCancel} aria-label="Close" className="text-muted-foreground hover:text-foreground cursor-pointer"><X size={18} weight="bold" /></button>
        </div>
        <p className="text-xs text-muted-foreground mb-3 flex items-center gap-1.5">
          <ArrowsOutCardinal size={14} /> Drag to reposition · use the slider to zoom
        </p>

        {/* Crop frame */}
        <div
          className="relative mx-auto overflow-hidden rounded-xl bg-secondary select-none touch-none cursor-grab active:cursor-grabbing"
          style={{ width: FRAME_W, height: frameH, maxWidth: '100%' }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
        >
          {imgUrl && img && (
            <img
              src={imgUrl}
              alt="Crop preview"
              draggable={false}
              style={{
                position: 'absolute',
                left: offset.x, top: offset.y,
                width: img.width * zoom, height: img.height * zoom,
                maxWidth: 'none',
              }}
            />
          )}
          {/* Rule-of-thirds grid + visible-area border — so the vendor sees
              exactly what shows on Browse and can line up their subject. */}
          <div className="pointer-events-none absolute inset-0 rounded-xl">
            <div className="absolute inset-0 border-2 border-white/80 rounded-xl" />
            {/* vertical thirds */}
            <div className="absolute top-0 bottom-0 left-1/3 w-px bg-white/40" />
            <div className="absolute top-0 bottom-0 left-2/3 w-px bg-white/40" />
            {/* horizontal thirds */}
            <div className="absolute left-0 right-0 top-1/3 h-px bg-white/40" />
            <div className="absolute left-0 right-0 top-2/3 h-px bg-white/40" />
            {/* label chip */}
            <span className="absolute top-2 left-2 bg-foreground/70 text-white text-[10px] font-semibold px-2 py-1 rounded-md">What customers see</span>
          </div>
        </div>

        {/* Zoom slider */}
        <div className="flex items-center gap-3 mt-4">
          <span className="text-xs text-muted-foreground">Zoom</span>
          <input
            type="range"
            min={minZoom} max={minZoom * 3} step="0.01"
            value={zoom} onChange={onZoom}
            className="flex-1 accent-primary cursor-pointer"
            data-testid="cover-zoom"
          />
        </div>

        <div className="flex gap-2 mt-5">
          <button onClick={onCancel} className="flex-1 border border-border bg-card text-foreground text-sm font-semibold py-2.5 rounded-xl btn-anim hover:bg-secondary/60 cursor-pointer">Cancel</button>
          <button onClick={apply} disabled={busy || !img} className="flex-1 bg-primary text-primary-foreground text-sm font-semibold py-2.5 rounded-xl btn-anim hover:opacity-95 cursor-pointer disabled:opacity-60" data-testid="cover-crop-apply">
            {busy ? 'Saving…' : 'Use this'}
          </button>
        </div>
      </div>
    </div>
  );
}
